import React, { Suspense } from 'react'
import { SkeletonTheme } from 'react-loading-skeleton'
import { createMemoryRouter, RouterProvider } from 'react-router-dom'

import { ApolloProvider, useReactiveVar } from '@apollo/client/react'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import PropTypes from 'prop-types'
import { changeLanguage, ErrorBoundary, ThemeProvider } from 'shared-components'

import { alertType, client, message, store } from './cache'
import i18n from './i18n'
import { routes } from './routes'

if (process.env.NODE_ENV === 'development') {
  // Adds messages only in a dev environment
  loadDevMessages()
  loadErrorMessages()
}
const DolphinBooking = ({
  locationId,
  partnerId,
  providerId,
  source,
  attributionData,
  lng,
}) => {
  React.useEffect(() => {
    changeLanguage(i18n, lng)
  }, [lng])

  React.useMemo(() => {
    store({
      locationId,
      partnerId,
      providerId,
      source,
      attributionData: attributionData,
    })
  }, [locationId, partnerId, providerId, source, attributionData])

  const router = React.useMemo(() => createMemoryRouter(routes), [routes])
  const snackBarMessage = useReactiveVar(message)
  const snackBarVariant = useReactiveVar(alertType)

  const handleSnackbarClose = () => {
    message(null)
    alertType(null)
  }

  return (
    <ErrorBoundary fallback={<div>Oops something went wrong...</div>}>
      <Suspense
        fallback={
          <SkeletonTheme
            baseColor="var(--intelibly-branding-neutral-200)"
            highlightColor="var(--intelibly-branding-neutral-50)"
          />
        }
      >
        <ApolloProvider client={client}>
          <ThemeProvider>
            <Snackbar
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              open={Boolean(snackBarMessage)}
              onClose={handleSnackbarClose}
              autoHideDuration={10000}
            >
              <Alert
                severity={snackBarVariant || 'success'}
                onClose={handleSnackbarClose}
                sx={{ width: '100%' }}
              >
                {snackBarMessage}
              </Alert>
            </Snackbar>
            <RouterProvider router={router} />
          </ThemeProvider>
        </ApolloProvider>
      </Suspense>
    </ErrorBoundary>
  )
}

DolphinBooking.propTypes = {
  locationId: PropTypes.string,
  practiceId: PropTypes.string,
  partnerId: PropTypes.string,
  providerId: PropTypes.string,
  source: PropTypes.string,
  lng: PropTypes.string,
  intiateRequestAppointment: PropTypes.func,
  attributionData: PropTypes.shape({
    utmSource: PropTypes.string,
    utmMedium: PropTypes.string,
    utmCampaign: PropTypes.string,
    utmTerm: PropTypes.string,
    utmContent: PropTypes.string,
    gclid: PropTypes.string,
    fbclid: PropTypes.string,
    msclkid: PropTypes.string,
    twclid: PropTypes.string,
    ttclid: PropTypes.string,
    liFatId: PropTypes.string,
  }),
}

export default DolphinBooking
